import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Container,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Snackbar,
  Tooltip,
  Badge
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  People as CapacityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format, addDays, isToday, isTomorrow, parseISO } from 'date-fns';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const FacilityAvailabilityPage = () => {
  const { t } = useTranslation();
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [facility, setFacility] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [duration, setDuration] = useState(60);
  
  // Session quota state
  const [sessionAvailability, setSessionAvailability] = useState([]);
  const [sessionQuotasEnabled, setSessionQuotasEnabled] = useState(false);

  const fetchFacility = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/facilities/${facilityId}`);
      setFacility(response.data.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch facility details');
      console.error('Error fetching facility:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  const fetchSessionAvailability = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/session-quotas/facility/${facilityId}/availability`, {
        params: {
          date: dateStr,
          userType: user?.userType
        }
      });
      setSessionAvailability(response.data.data.sessions || []);
      setSessionQuotasEnabled(response.data.data.facility.sessionQuotasEnabled);
    } catch (err) {
      console.error('Error fetching session availability:', err);
      setSessionAvailability([]);
    }
  }, [facilityId, selectedDate, user]);

  const fetchAvailability = useCallback(async () => {
    try {
      setAvailabilityLoading(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/facilities/${facilityId}/availability`, {
        params: { date: dateStr, duration }
      });
      setAvailability(response.data.data);
      setError('');

      // Also fetch session availability if quotas are enabled
      if (facility?.sessionQuotas?.enabled) {
        await fetchSessionAvailability();
      }
    } catch (err) {
      setError('Failed to fetch availability');
      console.error('Error fetching availability:', err);
      setAvailability(null);
    } finally {
      setAvailabilityLoading(false);
    }
  }, [facilityId, selectedDate, duration, facility, fetchSessionAvailability]);

  useEffect(() => {
    fetchFacility();
  }, [fetchFacility]);

  useEffect(() => {
    if (facility) {
      fetchAvailability();
    }
  }, [facility, fetchAvailability]);

  const handleSlotSelect = (slot) => {
    if (!user) {
      setSnackbar({
        open: true,
        message: 'Please log in to book a facility',
        severity: 'warning'
      });
      return;
    }
    setSelectedSlot(slot);
    setBookingDialog(true);
  };

  const handleBookingConfirm = async () => {
    if (!selectedSlot) return;

    try {
      setBookingLoading(true);
      const bookingData = {
        facility: facilityId,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        notes: bookingNotes.trim() || undefined
      };

      await api.post('/bookings', bookingData);
      
      setSnackbar({
        open: true,
        message: 'Booking created successfully!',
        severity: 'success'
      });
      
      setBookingDialog(false);
      setSelectedSlot(null);
      setBookingNotes('');
      
      // Refresh availability
      fetchAvailability();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to create booking';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Error creating booking:', err);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleDialogClose = () => {
    setBookingDialog(false);
    setSelectedSlot(null);
    setBookingNotes('');
  };

  const formatDateDisplay = (date) => {
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEEE, MMMM d');
  };

  const formatTimeSlot = (startTime, endTime) => {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
  };

  const getDurationOptions = () => {
    if (!facility?.bookingConfig) return [60];
    
    const { slotDuration, maxBookingDuration } = facility.bookingConfig;
    const options = [];
    
    for (let duration = slotDuration; duration <= maxBookingDuration; duration += slotDuration) {
      options.push(duration);
    }
    
    return options;
  };

  const getSessionQuotaInfo = (slotStartTime) => {
    if (!sessionQuotasEnabled || !user?.userType) return null;

    const slotTime = format(parseISO(slotStartTime), 'HH:mm');
    
    const matchingSession = sessionAvailability.find(session => {
      const sessionStart = session.startTime;
      const sessionEnd = session.endTime;
      
      // Check if slot time falls within session time range
      return slotTime >= sessionStart && slotTime < sessionEnd;
    });

    if (!matchingSession) return null;

    const userTypeInfo = matchingSession.availability[user.userType];
    if (!userTypeInfo) return null;

    return {
      session: matchingSession,
      userInfo: userTypeInfo,
      canBook: userTypeInfo.canBook,
      remaining: userTypeInfo.remaining,
      maxSlots: userTypeInfo.maxSlots,
      reason: userTypeInfo.reason
    };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!facility) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Facility not found</Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/facilities')}
            sx={{ mb: 2 }}
          >
            Back to Facilities
          </Button>
          
          <Typography variant="h4" component="h1" gutterBottom>
            Book {facility.name}
          </Typography>
          
          <Typography variant="body1" color="text.secondary">
            {facility.description}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Facility Info & Controls */}
          <Grid size={{xs:12 , md:4}}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Facility Details
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CapacityIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    Capacity: {facility.capacity} people
                  </Typography>
                </Box>

                <Chip
                  label={facility.status}
                  color={facility.status === 'active' ? 'success' : 'warning'}
                  size="small"
                  sx={{ textTransform: 'capitalize', mb: 2 }}
                />

                {facility.bookingConfig && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Booking Rules
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Slot Duration: {facility.bookingConfig.slotDuration} minutes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Advance Booking: {facility.bookingConfig.leadTime} hour(s)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Max Duration: {Math.floor(facility.bookingConfig.maxBookingDuration / 60)} hour(s)
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Date & Duration Selection */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('facilities:availability.title')}
                </Typography>
                
                <DatePicker
                  label={t('facilities:availability.labels.selectDate')}
                  value={selectedDate}
                  onChange={setSelectedDate}
                  minDate={new Date()}
                  maxDate={addDays(new Date(), 30)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal'
                    }
                  }}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    label={t('facilities:availability.labels.duration')}
                  >
                    {getDurationOptions().map((option) => (
                      <MenuItem key={option} value={option}>
                        {option} minutes ({Math.floor(option / 60)}h {option % 60}m)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Available Slots */}
          <Grid size={{xs:12 , md:8}}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ScheduleIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Available Slots - {formatDateDisplay(selectedDate)}
                  </Typography>
                </Box>

                {availabilityLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : availability ? (
                  <>
                    {availability.message ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {availability.message}
                      </Alert>
                    ) : null}

                    {availability.operatingHours && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Operating Hours: {availability.operatingHours.openTime} - {availability.operatingHours.closeTime}
                        </Typography>
                      </Box>
                    )}

                    {sessionQuotasEnabled && user && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Session Quota Information
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 4, height: 20, bgcolor: '#4caf50', borderRadius: 0.5 }} />
                            <Typography variant="caption">Available</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 4, height: 20, bgcolor: '#ff9800', borderRadius: 0.5 }} />
                            <Typography variant="caption">Limited (≤2 slots)</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 4, height: 20, bgcolor: '#f44336', borderRadius: 0.5 }} />
                            <Typography variant="caption">Full/Closed</Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Showing availability for {user.userType?.replace('_', ' ')} users
                        </Typography>
                      </Box>
                    )}

                    {availability.availableSlots && availability.availableSlots.length > 0 ? (
                      <Grid container spacing={2}>
                        {availability.availableSlots.map((slot, index) => {
                          const quotaInfo = getSessionQuotaInfo(slot.startTime);
                          const isSessionBlocked = quotaInfo && !quotaInfo.canBook;
                          
                          return (
                            <Grid size={{xs:12 , sm:6 , md:4}} key={index}>
                              <Tooltip 
                                title={quotaInfo ? 
                                  `${quotaInfo.remaining}/${quotaInfo.maxSlots} slots available for ${user?.userType || 'your user type'}${quotaInfo.reason && !quotaInfo.canBook ? ` - ${quotaInfo.reason}` : ''}` : 
                                  'Available for booking'
                                }
                                arrow
                              >
                                <Paper
                                  sx={{
                                    p: 2,
                                    cursor: isSessionBlocked ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s',
                                    opacity: isSessionBlocked ? 0.6 : 1,
                                    borderLeft: quotaInfo ? 
                                      `4px solid ${
                                        quotaInfo.canBook ? 
                                          (quotaInfo.remaining <= 2 ? '#ff9800' : '#4caf50') : 
                                          '#f44336'
                                      }` : 'none',
                                    '&:hover': isSessionBlocked ? {} : {
                                      bgcolor: 'primary.light',
                                      color: 'primary.contrastText',
                                      transform: 'translateY(-2px)',
                                      boxShadow: 2
                                    }
                                  }}
                                  onClick={() => !isSessionBlocked && handleSlotSelect(slot)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <TimeIcon sx={{ mr: 1, fontSize: 'small' }} />
                                      <Typography variant="body1" fontWeight="medium">
                                        {formatTimeSlot(slot.startTime, slot.endTime)}
                                      </Typography>
                                    </Box>
                                    {quotaInfo && (
                                      <Badge 
                                        badgeContent={quotaInfo.remaining} 
                                        color={
                                          quotaInfo.canBook ? 
                                            (quotaInfo.remaining <= 2 ? 'warning' : 'success') : 
                                            'error'
                                        }
                                        max={99}
                                      >
                                        <CapacityIcon fontSize="small" />
                                      </Badge>
                                    )}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {slot.duration} minutes
                                  </Typography>
                                  {quotaInfo && (
                                    <Box sx={{ mt: 1 }}>
                                      <Chip
                                        label={
                                          quotaInfo.canBook ? 
                                            `${quotaInfo.remaining} slots left` :
                                            quotaInfo.reason.includes('closed') ? 'Closed' : 'Full'
                                        }
                                        size="small"
                                        color={
                                          quotaInfo.canBook ? 
                                            (quotaInfo.remaining <= 2 ? 'warning' : 'success') : 
                                            'error'
                                        }
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Paper>
                              </Tooltip>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Alert severity="info">
                        No available slots for the selected date and duration.
                      </Alert>
                    )}
                  </>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Booking Confirmation Dialog */}
        <Dialog
          open={bookingDialog}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Confirm Booking
              <IconButton onClick={handleDialogClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {selectedSlot && (
              <>
                <Typography variant="h6" gutterBottom>
                  {facility.name}
                </Typography>
                
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={t('facilities:availability.table.date')}
                      secondary={format(selectedDate, 'EEEE, MMMM d, yyyy')}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={t('facilities:availability.table.time')}
                      secondary={formatTimeSlot(selectedSlot.startTime, selectedSlot.endTime)}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={t('facilities:availability.table.duration')}
                      secondary={`${selectedSlot.duration} minutes`}
                    />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  placeholder="Add any special requirements or notes..."
                  inputProps={{ maxLength: 500 }}
                  helperText={`${bookingNotes.length}/500 characters`}
                />
              </>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button
              onClick={handleBookingConfirm}
              variant="contained"
              disabled={bookingLoading}
            >
              {bookingLoading ? <CircularProgress size={20} /> : 'Confirm Booking'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default FacilityAvailabilityPage;